import React, { useEffect, useRef, useState, useCallback } from 'react';
import useRunner from './useRunner';
import { getLanguageByKey } from '../../constants/languages';
import {
    MdPlayArrow,
    MdStop,
    MdDeleteOutline,
    MdInput,
    MdExpandMore,
    MdExpandLess,
    MdClose,
} from 'react-icons/md';

/**
 * RunnerPanel — UI component for multi-language code execution output and stdin.
 *
 * Props:
 *   getCode:          () => string  — returns current editor code
 *   language:         string        — current language key
 *   onLanguageChange: (lang) => void — called when user picks a new language
 *   height:           number        — height of the panel
 *   isResizing:       boolean       — true if the panel is currently being resized
 *   onClose:          () => void    — closes the panel
 */
const RunnerPanel = ({ getCode, language, onLanguageChange, height, isResizing, onClose }) => {
    const { output, running, runCode, stopExecution, clearOutput } = useRunner();
    const outputEndRef = useRef(null);
    const [stdin, setStdin] = useState('');
    const [showStdin, setShowStdin] = useState(false);

    // Auto-scroll to bottom when new output appears
    useEffect(() => {
        outputEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [output]);

    const handleRun = useCallback(() => {
        const code = getCode();
        runCode(language, code, stdin);
    }, [getCode, language, runCode, stdin]);

    // Listen for custom trigger event from the sidebar button
    useEffect(() => {
        const handleTrigger = () => handleRun();
        window.addEventListener('triggerRun', handleTrigger);
        return () => window.removeEventListener('triggerRun', handleTrigger);
    }, [handleRun]);

    const currentLang = getLanguageByKey(language);

    const getLineColor = (type) => {
        switch (type) {
            case 'error':   return 'var(--runner-error)';
            case 'warn':    return 'var(--runner-warn)';
            case 'info':    return 'var(--runner-info)';
            case 'success': return 'var(--runner-success)';
            default:        return 'var(--text-primary)';
        }
    };

    const getLinePrefix = (type) => {
        switch (type) {
            case 'error':   return '✗';
            case 'warn':    return '⚠';
            case 'success': return '✓';
            default:        return '›';
        }
    };

    return (
        <div 
            className="codeRunnerPanel"
            style={height ? { height: `${height}px`, minHeight: '40px', maxHeight: 'none', transition: isResizing ? 'none' : undefined } : {}}
        >
            {/* ── Header: Language Status Badge + Actions ── */}
            <div className="codeRunnerHeader">
                <div className="codeRunnerHeaderLeft">
                    <span className="codeRunnerTitle">Console</span>
                    <span className="currentLangBadge">{currentLang.label}</span>
                </div>

                <div className="codeRunnerActions">
                    {/* Stdin Toggle */}
                    <button
                        className={`btn stdinToggleBtn ${showStdin ? 'active' : ''}`}
                        onClick={() => setShowStdin(!showStdin)}
                        title="Toggle Input (stdin)"
                        id="stdin-toggle"
                    >
                        <MdInput size={14} />
                        <span>Input</span>
                    </button>

                    {/* Run Button */}
                    <button
                        className="btn runBtn"
                        onClick={handleRun}
                        disabled={running}
                        id="run-code-btn"
                    >
                        {running ? (
                            <>
                                <span className="runBtnSpinner" />
                                Running...
                            </>
                        ) : (
                            <>
                                <MdPlayArrow size={16} />
                                Run
                            </>
                        )}
                    </button>

                    {/* Stop Button */}
                    {running && (
                        <button
                            className="btn stopBtn"
                            onClick={stopExecution}
                            id="stop-code-btn"
                        >
                            <MdStop size={14} />
                            Stop
                        </button>
                    )}

                    {/* Clear Button */}
                    <button
                        className="btn clearBtn"
                        onClick={clearOutput}
                        disabled={running}
                        id="clear-output-btn"
                        title="Clear Console"
                    >
                        <MdDeleteOutline size={14} />
                    </button>

                    {/* Close Button */}
                    {onClose && (
                        <button
                            className="btn closeBtn"
                            onClick={onClose}
                            title="Close Console"
                        >
                            <MdClose size={18} />
                        </button>
                    )}
                </div>
            </div>

            {/* ── Stdin Panel (collapsible) ── */}
            {showStdin && (
                <div className="stdinPanel">
                    <div className="stdinHeader">
                        <span className="stdinLabel">Standard Input (stdin)</span>
                        <button
                            className="stdinCollapseBtn"
                            onClick={() => setShowStdin(false)}
                        >
                            <MdExpandLess size={16} />
                        </button>
                    </div>
                    <textarea
                        className="stdinInput"
                        value={stdin}
                        onChange={(e) => setStdin(e.target.value)}
                        placeholder="Enter input values here (one per line)..."
                        rows={3}
                        id="stdin-input"
                    />
                </div>
            )}

            {/* ── Output Terminal ── */}
            <div className="codeRunnerOutput">
                {output.length === 0 ? (
                    <div className="codeRunnerPlaceholder">
                        <div className="placeholderIcon">▶</div>
                        <div>Click <strong>Run</strong> to execute your {currentLang.label} code</div>
                    </div>
                ) : (
                    output.map((line, i) => (
                        <div
                            key={i}
                            className={`codeRunnerLine ${line.type}`}
                        >
                            <span
                                className="codeRunnerLinePrefix"
                                style={{ color: getLineColor(line.type) }}
                            >
                                {getLinePrefix(line.type)}
                            </span>
                            <div className="codeRunnerLineContent">
                                {line.label && (
                                    <span
                                        className="codeRunnerLineLabel"
                                        style={{ color: getLineColor(line.type) }}
                                    >
                                        {line.label}
                                    </span>
                                )}
                                <pre className="codeRunnerLineText">{line.text}</pre>
                            </div>
                        </div>
                    ))
                )}
                <div ref={outputEndRef} />
            </div>
        </div>
    );
};

export default RunnerPanel;
